import { useState, useCallback, useMemo } from 'react';
import type {
  FacturaFormState,
  FacturaTotales,
  ProductoLinea,
  ClienteForm,
  MetodoPago,
  TipoEntrega,
  MetodoEntregaForm,
  TipoDescuento,
  TipoAjusteCredito,
  SentidoAjusteCredito,
  EstadoPago,
} from './facturaFormTypes';
import { lineEconomicBreakdown } from './lineEconomics';
import { aplicarAjusteCreditoAlTotal, metodoPagoEsCreditoSistema } from './ajusteCreditoTotal';

export function createEmptyFacturaFormState(): FacturaFormState {
  const d = new Date();
  const entrega = new Date();
  entrega.setDate(entrega.getDate() + 7);
  const primeraCuota = new Date();
  primeraCuota.setDate(primeraCuota.getDate() + 7);
  return {
    cliente: null,
    numero_factura: `TI-${Date.now().toString().slice(-9)}`,
    vendedor_id: undefined,
    fecha: d.toISOString().split('T')[0] ?? '',
    productos: [],
    descuento_tipo: 'ninguno',
    descuento_valor: 0,
    tipo_entrega: 'tienda',
    costo_envio: 0,
    metodo_entrega: 'recogida',
    notas_entrega: '',
    cobrar_envio_al_destino: false,
    fecha_entrega: entrega.toISOString().split('T')[0] ?? '',
    direccion_entrega: '',
    metodo_pago: 'efectivo',
    estado_pago: 'pendiente',
    agendar_calendario: false,
    es_regalo: false,
    notas: '',
    credito_num_cuotas: 1,
    credito_frecuencia_dias: 30,
    credito_primera_fecha: primeraCuota.toISOString().split('T')[0] ?? '',
    credito_ajuste_tipo: 'ninguno',
    credito_ajuste_valor: 0,
    credito_ajuste_sentido: 'descuento',
  };
}

export function useFacturaForm() {
  const [form, setForm] = useState<FacturaFormState>(() => createEmptyFacturaFormState());
  const [step, setStep] = useState(0);
  const TOTAL_STEPS = 3;

  const totales = useMemo((): FacturaTotales => {
    let sinIva = 0;
    let ivaAmt = 0;
    let conIva = 0;
    for (const p of form.productos) {
      const u = lineEconomicBreakdown(p);
      sinIva += u.subtotalSinIva;
      ivaAmt += u.totalIva;
      conIva += u.subtotalConIva;
    }
    let descuento = 0;
    if (form.descuento_tipo === 'porcentaje') {
      descuento = conIva * (form.descuento_valor / 100);
    } else if (form.descuento_tipo === 'valor') {
      descuento = Math.min(form.descuento_valor, conIva);
    }
    const factor = conIva > 0 ? Math.max(0, (conIva - descuento) / conIva) : 0;
    const costoEnvio = Math.max(0, Math.round(Number(form.costo_envio) || 0));
    const envioEnTotal = form.cobrar_envio_al_destino ? 0 : costoEnvio;
    const baseTotal = conIva - descuento + envioEnTotal;
    const { ajuste, total } = aplicarAjusteCreditoAlTotal(baseTotal, {
      aplicar: metodoPagoEsCreditoSistema(form.metodo_pago),
      tipo: form.credito_ajuste_tipo,
      valor: form.credito_ajuste_valor,
      sentido: form.credito_ajuste_sentido,
    });
    return {
      subtotal: conIva,
      descuento,
      base_iva: sinIva * factor,
      iva: ivaAmt * factor,
      costo_envio: costoEnvio,
      ajuste_credito: ajuste,
      total,
    };
  }, [
    form.productos,
    form.descuento_tipo,
    form.descuento_valor,
    form.costo_envio,
    form.cobrar_envio_al_destino,
    form.metodo_pago,
    form.credito_ajuste_tipo,
    form.credito_ajuste_valor,
    form.credito_ajuste_sentido,
  ]);

  const goNext = useCallback(() => {
    setStep((s) => (s < TOTAL_STEPS - 1 ? s + 1 : s));
  }, [TOTAL_STEPS]);

  const goBack = useCallback(() => {
    setStep((s) => (s > 0 ? s - 1 : 0));
  }, []);

  const reset = useCallback(() => {
    setForm(createEmptyFacturaFormState());
    setStep(0);
  }, []);

  const replaceForm = useCallback((next: FacturaFormState) => {
    setForm(next);
    setStep(0);
  }, []);

  const setCliente = useCallback((cliente: ClienteForm | null) => {
    setForm((f) => ({ ...f, cliente }));
  }, []);

  const setNumeroFactura = useCallback((numero_factura: string) => {
    setForm((f) => ({ ...f, numero_factura }));
  }, []);

  const setVendedor = useCallback((vendedor_id: string) => {
    setForm((f) => ({ ...f, vendedor_id }));
  }, []);

  const addProducto = useCallback((producto: Omit<ProductoLinea, 'qty'>) => {
    setForm((f) => ({
      ...f,
      productos: [...f.productos, { ...producto, qty: 1 }],
    }));
  }, []);

  const setProductos = useCallback((productos: ProductoLinea[]) => {
    setForm((f) => ({ ...f, productos }));
  }, []);

  const removeProducto = useCallback((id: string) => {
    setForm((f) => ({
      ...f,
      productos: f.productos.filter((p) => p.id !== id),
    }));
  }, []);

  const updateQty = useCallback((id: string, delta: number) => {
    setForm((f) => ({
      ...f,
      productos: f.productos.map((p) =>
        p.id === id ? { ...p, qty: Math.max(1, p.qty + delta) } : p
      ),
    }));
  }, []);

  const setDescuento = useCallback((tipo: TipoDescuento, valor: number = 0) => {
    setForm((f) => ({ ...f, descuento_tipo: tipo, descuento_valor: valor }));
  }, []);

  const setDescuentoValor = useCallback((descuento_valor: number) => {
    setForm((f) => ({ ...f, descuento_valor }));
  }, []);

  const setTipoEntrega = useCallback((tipo_entrega: TipoEntrega) => {
    setForm((f) => ({
      ...f,
      tipo_entrega,
      metodo_entrega:
        tipo_entrega === 'domicilio'
          ? f.metodo_entrega === 'recogida'
            ? 'entrega_local'
            : f.metodo_entrega
          : 'recogida',
      ...(tipo_entrega === 'tienda' ? { costo_envio: 0 } : {}),
    }));
  }, []);

  const setCostoEnvio = useCallback((costo_envio: number) => {
    setForm((f) => ({ ...f, costo_envio: Math.max(0, Math.round(costo_envio)) }));
  }, []);

  const setMetodoEntrega = useCallback((metodo_entrega: MetodoEntregaForm) => {
    setForm((f) => ({
      ...f,
      metodo_entrega,
      ...(metodo_entrega === 'recogida' ? { costo_envio: 0 } : {}),
    }));
  }, []);

  const setNotasEntrega = useCallback((notas_entrega: string) => {
    setForm((f) => ({ ...f, notas_entrega }));
  }, []);

  const setCobrarEnvioAlDestino = useCallback((cobrar_envio_al_destino: boolean) => {
    setForm((f) => ({ ...f, cobrar_envio_al_destino }));
  }, []);

  const setFechaEntrega = useCallback((fecha_entrega: string) => {
    setForm((f) => ({ ...f, fecha_entrega }));
  }, []);

  const setDireccionEntrega = useCallback((direccion_entrega: string) => {
    setForm((f) => ({ ...f, direccion_entrega }));
  }, []);

  const setMetodoPago = useCallback((metodo_pago: MetodoPago) => {
    setForm((f) => ({ ...f, metodo_pago }));
  }, []);

  const setEstadoPago = useCallback((estado_pago: EstadoPago) => {
    setForm((f) => ({ ...f, estado_pago: estado_pago }));
  }, []);

  const toggleCalendario = useCallback(() => {
    setForm((f) => ({ ...f, agendar_calendario: !f.agendar_calendario }));
  }, []);

  const setAgendarCalendario = useCallback((v: boolean) => {
    setForm((f) => ({ ...f, agendar_calendario: v }));
  }, []);

  const toggleRegalo = useCallback(() => {
    setForm((f) => ({ ...f, es_regalo: !f.es_regalo }));
  }, []);

  const setNotas = useCallback((notas: string) => {
    setForm((f) => ({ ...f, notas }));
  }, []);

  const setCreditoNumCuotas = useCallback((credito_num_cuotas: number) => {
    setForm((f) => ({ ...f, credito_num_cuotas }));
  }, []);

  const setCreditoFrecuenciaDias = useCallback((credito_frecuencia_dias: 7 | 15 | 30) => {
    setForm((f) => ({ ...f, credito_frecuencia_dias }));
  }, []);

  const setCreditoPrimeraFecha = useCallback((credito_primera_fecha: string) => {
    setForm((f) => ({ ...f, credito_primera_fecha }));
  }, []);

  const setCreditoAjusteTipo = useCallback((credito_ajuste_tipo: TipoAjusteCredito) => {
    setForm((f) => ({
      ...f,
      credito_ajuste_tipo,
      credito_ajuste_valor: credito_ajuste_tipo === 'ninguno' ? 0 : f.credito_ajuste_valor,
    }));
  }, []);

  const setCreditoAjusteValor = useCallback((credito_ajuste_valor: number) => {
    setForm((f) => ({ ...f, credito_ajuste_valor: Math.max(0, Number(credito_ajuste_valor) || 0) }));
  }, []);

  const setCreditoAjusteSentido = useCallback((credito_ajuste_sentido: SentidoAjusteCredito) => {
    setForm((f) => ({ ...f, credito_ajuste_sentido }));
  }, []);

  const canProceed = useMemo(() => {
    if (step === 0) {
      return !!form.cliente && form.productos.length > 0;
    }
    if (step === 1) {
      if (form.tipo_entrega === 'domicilio' && !form.direccion_entrega?.trim()) return false;
      const credSystemId =
        form.metodo_pago === '__credito__' || form.metodo_pago === '__clientelo_credito__';
      if (credSystemId) {
        const pr = String(form.credito_primera_fecha || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(pr)) return false;
        const n = Math.floor(Number(form.credito_num_cuotas) || 0);
        if (n < 1 || n > 36) return false;
      }
      return true;
    }
    return true;
  }, [step, form]);

  return {
    form,
    step,
    totales,
    canProceed,
    TOTAL_STEPS,
    goNext,
    goBack,
    reset,
    replaceForm,
    setCliente,
    setNumeroFactura,
    setVendedor,
    addProducto,
    setProductos,
    removeProducto,
    updateQty,
    setDescuento,
    setDescuentoValor,
    setTipoEntrega,
    setCostoEnvio,
    setMetodoEntrega,
    setNotasEntrega,
    setCobrarEnvioAlDestino,
    setFechaEntrega,
    setDireccionEntrega,
    setMetodoPago,
    setEstadoPago,
    toggleCalendario,
    setAgendarCalendario,
    toggleRegalo,
    setNotas,
    setCreditoNumCuotas,
    setCreditoFrecuenciaDias,
    setCreditoPrimeraFecha,
    setCreditoAjusteTipo,
    setCreditoAjusteValor,
    setCreditoAjusteSentido,
  };
}

export type UseFacturaFormReturn = ReturnType<typeof useFacturaForm>;
